/**
 * 势力状态管理
 * 管理势力实例、外交关系、联姻等
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  FactionInstance,
  FactionArchive,
  FactionRelation,
  GameDate,
  MarriageAlliance,
  WarState,
  TreatyProposal,
  MarriageProposal,
  RelationType,
  RelationMetrics,
  AIFactionAttitude,
  AIMarriageEvaluation,
} from '../types/faction'
import { getFactionTemplate } from '../data/factionTemplates'

// ============================================
// 状态定义
// ============================================

interface FactionState {
  // 活跃势力
  activeFactions: Map<string, FactionInstance>
  
  // 已灭亡势力档案
  factionArchives: Map<string, FactionArchive>
  
  // 玩家势力ID
  playerFactionId: string | null
  
  // 待处理提议
  pendingProposals: Map<string, TreatyProposal | MarriageProposal>
  
  // 当前战争
  activeWars: Map<string, WarState>
  
  // ID计数器
  idCounter: number
  
  // 当前游戏时间
  currentDate: GameDate
}

interface FactionActions {
  // 初始化
  initializeFactions: (scenarioId: string, playerTemplateId: string, startDate: GameDate) => void
  resetFactions: () => void
  
  // 势力操作
  createFaction: (templateId: string, cityIds: string[], date: GameDate, customName?: string) => string
  destroyFaction: (factionId: string, reason: string, date: GameDate, conquerorId?: string) => void
  updateFaction: (factionId: string, updates: Partial<FactionInstance>) => void
  
  // 外交操作
  establishContact: (factionAId: string, factionBId: string, context: string, date: GameDate) => void
  changeRelationType: (fromId: string, toId: string, newType: RelationType, reason: string, date: GameDate) => void
  updateRelationMetrics: (fromId: string, toId: string, updates: Partial<RelationMetrics>) => void
  
  // 联姻操作
  proposeMarriage: (fromId: string, toId: string, ourCharacterId: string, date: GameDate) => string
  acceptMarriage: (proposalId: string, date: GameDate) => void
  rejectMarriage: (proposalId: string, reason?: string) => void
  breakMarriage: (factionAId: string, factionBId: string, reason: import('../types/faction').MarriageBreakReason, date: GameDate) => void
  
  // 战争操作
  declareWar: (aggressorId: string, defenderId: string, casusBelli: string, date: GameDate) => string
  endWar: (warId: string, winnerId: string | null, date: GameDate) => void
  
  // 查询
  getFaction: (factionId: string) => FactionInstance | undefined
  getPlayerFaction: () => FactionInstance | undefined
  getRelation: (factionAId: string, factionBId: string) => FactionRelation | null
  getAllies: (factionId: string) => string[]
  getEnemies: (factionId: string) => string[]
  getVassals: (factionId: string) => string[]
  getSuzerain: (factionId: string) => string | null
  getInLaws: (factionId: string) => string[]
  
  // AI相关
  evaluateAttitude: (aiFactionId: string, targetFactionId: string) => AIFactionAttitude
  evaluateMarriageProposal: (proposal: MarriageProposal) => AIMarriageEvaluation
  
  // 序列化/反序列化
  serialize: () => string
  deserialize: (data: string) => void
}

// ============================================
// 辅助函数
// ============================================

function generateId(counter: number): string {
  return `faction_${Date.now()}_${counter}`
}

function createDefaultRelation(targetId: string, date: GameDate): FactionRelation {
  return {
    targetFactionId: targetId,
    type: 'unknown',
    metrics: {
      awareness: 0,
      opinion: 0,
      trust: 0,
      fear: 0,
      interest: 0,
    },
    history: {
      transitions: [],
      keyEvents: [],
      treaties: [],
    },
    interactions: {},
    since: date,
    lastUpdate: date,
  }
}

function createInitialFaction(
  templateId: string,
  factionId: string,
  cityIds: string[],
  date: GameDate,
  customName?: string
): FactionInstance {
  const template = getFactionTemplate(templateId)
  if (!template) {
    throw new Error(`Template not found: ${templateId}`)
  }
  
  return {
    instanceId: factionId,
    templateId,
    name: customName || template.baseProfile.name,
    type: template.baseProfile.type,
    color: template.baseProfile.color,
    emblem: template.baseProfile.emblem,
    status: 'active',
    foundedAt: date,
    ruler: {
      name: customName ? `${customName}君` : `${template.baseProfile.name}首领`,
      title: template.baseProfile.type === 'royal' ? '王' : 
             template.baseProfile.type === 'vassal' ? '侯' : '首领',
      traits: [],
      reignStart: date.year,
    },
    resources: {
      ...template.baseResources,
      military: Math.floor(template.baseResources.population / 10),
    },
    ownedCityIds: cityIds,
    capitalCityId: cityIds[0] || '',
    relations: {},
    vassalIds: [],
    technologies: [],
    policies: [],
    traits: template.traitTemplates.map(t => t.traitId),
    history: [{
      id: `event_${Date.now()}`,
      type: 'founded',
      year: date.year,
      month: date.month,
      title: `${customName || template.baseProfile.name}建立`,
      description: `${customName || template.baseProfile.name}于${date.year}年建立`,
    }],
  }
}

// ============================================
// Store 创建
// ============================================

export const useFactionStore = create<FactionState & FactionActions>()(
  persist(
    (set, get) => ({
      // 初始状态
      activeFactions: new Map(),
      factionArchives: new Map(),
      playerFactionId: null,
      pendingProposals: new Map(),
      activeWars: new Map(),
      idCounter: 0,
      currentDate: { year: -1046, month: 1, day: 1 },

      // ========================================
      // 初始化
      // ========================================
      
      initializeFactions: (_scenarioId, playerTemplateId, startDate) => {
        const state = get()
        const newFactions = new Map<string, FactionInstance>()
        let counter = state.idCounter
        
        // 根据剧本创建初始势力
        // TODO: 根据 scenarioId 加载剧本配置
        const initialTemplates = ['shang', 'zhou', 'guifang', 'qiangfang', 'renfang']
        
        initialTemplates.forEach(templateId => {
          const factionId = generateId(counter++)
          // TODO: 根据剧本配置分配城邑
          const cityIds: string[] = []
          
          const faction = createInitialFaction(
            templateId,
            factionId,
            cityIds,
            startDate,
            undefined
          )
          
          newFactions.set(factionId, faction)
          
          // 设置玩家势力
          if (templateId === playerTemplateId) {
            set({ playerFactionId: factionId })
          }
        })
        
        set({
          activeFactions: newFactions,
          idCounter: counter,
          currentDate: startDate,
        })
      },
      
      resetFactions: () => {
        set({
          activeFactions: new Map(),
          factionArchives: new Map(),
          playerFactionId: null,
          pendingProposals: new Map(),
          activeWars: new Map(),
          idCounter: 0,
          currentDate: { year: -1046, month: 1, day: 1 },
        })
      },
      
      // ========================================
      // 势力操作
      // ========================================
      
      createFaction: (templateId, cityIds, date, customName) => {
        const state = get()
        const factionId = generateId(state.idCounter)
        
        const faction = createInitialFaction(templateId, factionId, cityIds, date, customName)
        
        const newFactions = new Map(state.activeFactions)
        newFactions.set(factionId, faction)
        
        set({
          activeFactions: newFactions,
          idCounter: state.idCounter + 1,
        })
        
        return factionId
      },
      
      destroyFaction: (factionId, _reason, date, conquerorId) => {
        const state = get()
        const faction = state.activeFactions.get(factionId)
        if (!faction) return
        
        // 创建档案
        const archive: FactionArchive = {
          archiveId: `archive_${factionId}`,
          originalInstanceId: factionId,
          templateId: faction.templateId,
          finalName: faction.name,
          factionType: faction.type,
          existedPeriod: {
            from: faction.foundedAt,
            to: date,
          },
          finalRuler: {
            name: faction.ruler.name,
            title: faction.ruler.title,
            reignYears: date.year - faction.ruler.reignStart,
          },
          statistics: {
            maxCities: faction.ownedCityIds.length,
            peakPrestige: faction.resources.prestige,
            totalWars: 0, // TODO: 计算战争统计
            warsWon: 0,
            warsLost: 0,
            citiesConquered: 0,
            citiesLost: 0,
            rulersCount: 1,
          },
          fallReason: conquerorId ? 'conquered' : 'collapse',
          conqueredBy: conquerorId ? {
            factionId: conquerorId,
            factionName: state.activeFactions.get(conquerorId)?.name || '未知',
            year: date.year,
          } : undefined,
          fullHistory: [...faction.history, {
            id: `event_${Date.now()}`,
            type: 'destroyed',
            year: date.year,
            month: date.month,
            title: `${faction.name}灭亡`,
            description: `${faction.name}于${date.year}年灭亡`,
            relatedFactionId: conquerorId,
          }],
          legacyScore: faction.resources.prestige + faction.ownedCityIds.length * 10,
        }
        
        // 更新状态
        const newFactions = new Map(state.activeFactions)
        newFactions.delete(factionId)
        
        const newArchives = new Map(state.factionArchives)
        newArchives.set(archive.archiveId, archive)
        
        set({
          activeFactions: newFactions,
          factionArchives: newArchives,
        })
      },
      
      updateFaction: (factionId, updates) => {
        const state = get()
        const faction = state.activeFactions.get(factionId)
        if (!faction) return
        
        const newFactions = new Map(state.activeFactions)
        newFactions.set(factionId, { ...faction, ...updates })
        
        set({ activeFactions: newFactions })
      },
      
      // ========================================
      // 外交操作
      // ========================================
      
      establishContact: (factionAId, factionBId, context, date) => {
        const state = get()
        const factionA = state.activeFactions.get(factionAId)
        const factionB = state.activeFactions.get(factionBId)
        if (!factionA || !factionB) return
        
        // 创建双向关系
        const relationA = createDefaultRelation(factionBId, date)
        relationA.type = 'neutral'
        relationA.history.keyEvents.push({
          id: `event_${Date.now()}`,
          type: 'first_contact',
          year: date.year,
          month: date.month,
          title: '首次接触',
          description: `通过${context}与${factionB.name}建立联系`,
          impact: 0,
        })
        
        const relationB = createDefaultRelation(factionAId, date)
        relationB.type = 'neutral'
        relationB.history.keyEvents.push({
          id: `event_${Date.now()}_b`,
          type: 'first_contact',
          year: date.year,
          month: date.month,
          title: '首次接触',
          description: `通过${context}与${factionA.name}建立联系`,
          impact: 0,
        })
        
        // 更新双方关系
        const newFactions = new Map(state.activeFactions)
        newFactions.set(factionAId, {
          ...factionA,
          relations: { ...factionA.relations, [factionBId]: relationA },
        })
        newFactions.set(factionBId, {
          ...factionB,
          relations: { ...factionB.relations, [factionAId]: relationB },
        })
        
        set({ activeFactions: newFactions })
      },
      
      changeRelationType: (fromId, toId, newType, reason, date) => {
        const state = get()
        const fromFaction = state.activeFactions.get(fromId)
        const toFaction = state.activeFactions.get(toId)
        if (!fromFaction || !toFaction) return
        
        const oldRelation = fromFaction.relations[toId]
        const oldType = oldRelation?.type || 'unknown'
        
        // 更新关系类型
        const newRelation: FactionRelation = {
          ...(oldRelation || createDefaultRelation(toId, date)),
          type: newType,
          lastUpdate: date,
          history: {
            ...((oldRelation || createDefaultRelation(toId, date)).history),
            transitions: [
              ...(oldRelation?.history.transitions || []),
              {
                from: oldType,
                to: newType,
                year: date.year,
                month: date.month,
                reason,
              },
            ],
          },
        }
        
        // 更新双方
        const newFactions = new Map(state.activeFactions)
        newFactions.set(fromId, {
          ...fromFaction,
          relations: { ...fromFaction.relations, [toId]: newRelation },
        })
        
        // 反向关系
        const reverseType = getReverseRelationType(newType)
        const toRelation = toFaction.relations[fromId]
        newFactions.set(toId, {
          ...toFaction,
          relations: {
            ...toFaction.relations,
            [fromId]: {
              ...(toRelation || createDefaultRelation(fromId, date)),
              type: reverseType,
              lastUpdate: date,
            },
          },
        })
        
        set({ activeFactions: newFactions })
      },
      
      updateRelationMetrics: (fromId, toId, updates) => {
        const state = get()
        const fromFaction = state.activeFactions.get(fromId)
        if (!fromFaction) return
        
        const relation = fromFaction.relations[toId]
        if (!relation) return
        
        const newFactions = new Map(state.activeFactions)
        newFactions.set(fromId, {
          ...fromFaction,
          relations: {
            ...fromFaction.relations,
            [toId]: {
              ...relation,
              metrics: { ...relation.metrics, ...updates },
            },
          },
        })
        
        set({ activeFactions: newFactions })
      },
      
      // ========================================
      // 联姻操作
      // ========================================
      
      proposeMarriage: (fromId, toId, ourCharacterId, date) => {
        const state = get()
        const proposalId = `proposal_${Date.now()}`
        
        // TODO: 获取人物信息
        const proposal: MarriageProposal = {
          id: proposalId,
          fromFactionId: fromId,
          toFactionId: toId,
          proposedAt: date,
          expiresAt: { year: date.year, month: date.month + 3, day: 1 }, // 3个月有效期
          status: 'pending',
          ourOffer: {
            characterId: ourCharacterId,
            characterName: '待实现', // TODO: 从人物系统获取
            isRuler: false,
            age: 20,
            gender: 'female',
          },
          terms: {
            allianceDuration: 60, // 5年同盟
          },
        }
        
        const newProposals = new Map(state.pendingProposals)
        newProposals.set(proposalId, proposal)
        
        set({ pendingProposals: newProposals })
        
        return proposalId
      },
      
      acceptMarriage: (proposalId, date) => {
        const state = get()
        const proposal = state.pendingProposals.get(proposalId) as MarriageProposal
        if (!proposal || proposal.status !== 'pending') return
        
        // 创建姻亲关系
        const marriageAlliance: MarriageAlliance = {
          establishedAt: date,
          ourSide: {
            factionId: proposal.fromFactionId,
            characterId: proposal.ourOffer.characterId,
            characterName: proposal.ourOffer.characterName,
            gender: proposal.ourOffer.gender,
            age: proposal.ourOffer.age,
            isRuler: proposal.ourOffer.isRuler,
          },
          theirSide: {
            factionId: proposal.toFactionId,
            characterId: 'temp', // TODO: 从提议中获取
            characterName: '待实现',
            gender: proposal.ourOffer.gender === 'male' ? 'female' : 'male',
            age: 20,
            isRuler: false,
          },
          effects: {
            opinionBonus: 30,
            trustBonus: 20,
            allianceDuration: proposal.terms.allianceDuration,
          },
          status: 'active',
        }
        
        // 更新双方关系
        const fromFaction = state.activeFactions.get(proposal.fromFactionId)
        const toFaction = state.activeFactions.get(proposal.toFactionId)
        if (!fromFaction || !toFaction) return
        
        const newFactions = new Map(state.activeFactions)
        
        // 更新提议方关系
        const fromRelation = fromFaction.relations[proposal.toFactionId] || 
          createDefaultRelation(proposal.toFactionId, date)
        newFactions.set(proposal.fromFactionId, {
          ...fromFaction,
          relations: {
            ...fromFaction.relations,
            [proposal.toFactionId]: {
              ...fromRelation,
              type: 'in_laws',
              interactions: {
                ...fromRelation.interactions,
                marriageAlliance,
              },
            },
          },
        })
        
        // 更新接受方关系
        const toRelation = toFaction.relations[proposal.fromFactionId] ||
          createDefaultRelation(proposal.fromFactionId, date)
        newFactions.set(proposal.toFactionId, {
          ...toFaction,
          relations: {
            ...toFaction.relations,
            [proposal.fromFactionId]: {
              ...toRelation,
              type: 'in_laws',
              interactions: {
                ...toRelation.interactions,
                marriageAlliance: {
                  ...marriageAlliance,
                  ourSide: marriageAlliance.theirSide,
                  theirSide: marriageAlliance.ourSide,
                },
              },
            },
          },
        })
        
        // 更新提议状态
        const newProposals = new Map(state.pendingProposals)
        newProposals.set(proposalId, { ...proposal, status: 'accepted' })
        
        set({
          activeFactions: newFactions,
          pendingProposals: newProposals,
        })
      },
      
      rejectMarriage: (proposalId, _reason) => {
        const state = get()
        const proposal = state.pendingProposals.get(proposalId)
        if (!proposal || proposal.status !== 'pending') return
        
        const newProposals = new Map(state.pendingProposals)
        newProposals.set(proposalId, { ...proposal, status: 'rejected' })
        
        set({ pendingProposals: newProposals })
      },
      
      breakMarriage: (factionAId, factionBId, _reason: import('../types/faction').MarriageBreakReason, date) => {
        const state = get()
        const factionA = state.activeFactions.get(factionAId)
        const factionB = state.activeFactions.get(factionBId)
        if (!factionA || !factionB) return

        // 更新关系为敌对
        const newFactions = new Map(state.activeFactions)

        const relationA = factionA.relations[factionBId]
        if (relationA) {
          const updatedInteractions = { ...relationA.interactions }
          if (updatedInteractions.marriageAlliance) {
            updatedInteractions.marriageAlliance = {
              ...updatedInteractions.marriageAlliance,
              status: 'broken',
              brokenAt: date,
              brokenReason: _reason,
            }
          }

          const updatedRelationA: FactionRelation = {
            ...relationA,
            type: 'hostile',
            interactions: updatedInteractions,
          }

          newFactions.set(factionAId, {
            ...factionA,
            relations: {
              ...factionA.relations,
              [factionBId]: updatedRelationA,
            },
          })
        }

        const relationB = factionB.relations[factionAId]
        if (relationB) {
          const updatedInteractionsB = { ...relationB.interactions }
          if (updatedInteractionsB.marriageAlliance) {
            updatedInteractionsB.marriageAlliance = {
              ...updatedInteractionsB.marriageAlliance,
              status: 'broken',
              brokenAt: date,
              brokenReason: _reason,
            }
          }

          const updatedRelationB: FactionRelation = {
            ...relationB,
            type: 'hostile',
            interactions: updatedInteractionsB,
          }

          newFactions.set(factionBId, {
            ...factionB,
            relations: {
              ...factionB.relations,
              [factionAId]: updatedRelationB,
            },
          })
        }

        set({ activeFactions: newFactions })
      },
      
      // ========================================
      // 战争操作
      // ========================================
      
      declareWar: (aggressorId, defenderId, casusBelli, date) => {
        const state = get()
        const warId = `war_${Date.now()}`
        
        const war: WarState = {
          warId,
          declaredAt: date,
          casusBelli,
          warGoals: [],
          allies: [aggressorId],
          theirAllies: [defenderId],
          battles: [],
          warScore: 0,
        }
        
        // 更新关系为交战
        get().changeRelationType(aggressorId, defenderId, 'war', casusBelli, date)
        
        const newWars = new Map(state.activeWars)
        newWars.set(warId, war)
        
        set({ activeWars: newWars })
        
        return warId
      },
      
      endWar: (warId, winnerId, date) => {
        const state = get()
        const war = state.activeWars.get(warId)
        if (!war) return
        
        // 更新参战方关系
        war.allies.forEach(allyId => {
          war.theirAllies.forEach(enemyId => {
            get().changeRelationType(
              allyId,
              enemyId,
              winnerId === null ? 'neutral' : winnerId === allyId ? 'hostile' : 'vassal',
              '战争结束',
              date
            )
          })
        })
        
        const newWars = new Map(state.activeWars)
        newWars.delete(warId)
        
        set({ activeWars: newWars })
      },
      
      // ========================================
      // 查询
      // ========================================
      
      getFaction: (factionId) => {
        return get().activeFactions.get(factionId)
      },
      
      getPlayerFaction: () => {
        const { playerFactionId, activeFactions } = get()
        return playerFactionId ? activeFactions.get(playerFactionId) : undefined
      },
      
      getRelation: (factionAId, factionBId) => {
        const factionA = get().activeFactions.get(factionAId)
        return factionA?.relations[factionBId] || null
      },
      
      getAllies: (factionId) => {
        const faction = get().activeFactions.get(factionId)
        if (!faction) return []
        return Object.entries(faction.relations)
          .filter(([_, rel]) => rel.type === 'ally')
          .map(([id, _]) => id)
      },
      
      getEnemies: (factionId) => {
        const faction = get().activeFactions.get(factionId)
        if (!faction) return []
        return Object.entries(faction.relations)
          .filter(([_, rel]) => rel.type === 'war')
          .map(([id, _]) => id)
      },
      
      getVassals: (factionId) => {
        const faction = get().activeFactions.get(factionId)
        return faction?.vassalIds || []
      },
      
      getSuzerain: (factionId) => {
        const faction = get().activeFactions.get(factionId)
        return faction?.suzerainId || null
      },
      
      getInLaws: (factionId) => {
        const faction = get().activeFactions.get(factionId)
        if (!faction) return []
        return Object.entries(faction.relations)
          .filter(([_, rel]) => rel.type === 'in_laws')
          .map(([id, _]) => id)
      },
      
      // ========================================
      // AI相关
      // ========================================
      
      evaluateAttitude: (aiFactionId, targetFactionId) => {
        const state = get()
        const aiFaction = state.activeFactions.get(aiFactionId)
        const targetFaction = state.activeFactions.get(targetFactionId)
        
        if (!aiFaction || !targetFaction) {
          return {
            threatLevel: 0,
            desirability: 0,
            shouldAttack: false,
            shouldAlly: false,
          }
        }
        
        // 基础评估
        const relation = aiFaction.relations[targetFactionId]
        const opinion = relation?.metrics.opinion || 0
        const militaryRatio = targetFaction.resources.military / 
          (aiFaction.resources.military || 1)
        
        return {
          threatLevel: Math.min(100, Math.max(0, militaryRatio * 50 - opinion * 0.3)),
          desirability: Math.min(100, Math.max(0, opinion + 50)),
          shouldAttack: militaryRatio < 0.8 && opinion < -30,
          shouldAlly: militaryRatio > 1.2 && opinion > 30,
        }
      },
      
      evaluateMarriageProposal: (proposal) => {
        const state = get()
        const toFaction = state.activeFactions.get(proposal.toFactionId)
        const fromFaction = state.activeFactions.get(proposal.fromFactionId)
        
        if (!toFaction || !fromFaction) {
          return { acceptance: 0, reason: '势力不存在' }
        }
        
        const relation = toFaction.relations[proposal.fromFactionId]
        const opinion = relation?.metrics.opinion || 0
        
        // 基础接受度
        let acceptance = 30 + opinion * 0.5
        
        // 根据对方实力调整
        const powerRatio = fromFaction.resources.prestige / 
          (toFaction.resources.prestige || 1)
        acceptance += (powerRatio - 1) * 20
        
        // 边界检查
        acceptance = Math.min(100, Math.max(0, acceptance))
        
        return {
          acceptance,
          reason: acceptance > 50 ? '有利可图' : '时机未到',
        }
      },
      
      // ========================================
      // 序列化/反序列化
      // ========================================
      
      serialize: () => {
        const state = get()
        return JSON.stringify({
          activeFactions: Array.from(state.activeFactions.entries()),
          factionArchives: Array.from(state.factionArchives.entries()),
          playerFactionId: state.playerFactionId,
          pendingProposals: Array.from(state.pendingProposals.entries()),
          activeWars: Array.from(state.activeWars.entries()),
          idCounter: state.idCounter,
          currentDate: state.currentDate,
        })
      },
      
      deserialize: (data) => {
        const parsed = JSON.parse(data)
        set({
          activeFactions: new Map(parsed.activeFactions),
          factionArchives: new Map(parsed.factionArchives),
          playerFactionId: parsed.playerFactionId,
          pendingProposals: new Map(parsed.pendingProposals),
          activeWars: new Map(parsed.activeWars),
          idCounter: parsed.idCounter,
          currentDate: parsed.currentDate,
        })
      },
    }),
    {
      name: 'faction-store',
      partialize: (state) => ({
        // 只持久化必要数据
        idCounter: state.idCounter,
      }),
    }
  )
)

// ============================================
// 辅助函数
// ============================================

function getReverseRelationType(type: RelationType): RelationType {
  const reverseMap: Record<RelationType, RelationType> = {
    'unknown': 'unknown',
    'neutral': 'neutral',
    'friendly': 'friendly',
    'ally': 'ally',
    'hostile': 'hostile',
    'war': 'war',
    'vassal': 'suzerain',
    'suzerain': 'vassal',
    'tributary': 'liege',
    'liege': 'tributary',
    'in_laws': 'in_laws',
  }
  return reverseMap[type] || type
}
